'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './button';
import {
  fieldControlClassName,
  fieldDensityClassName,
  type FieldControlSize,
  type FieldDensity,
} from './input';
import { useFloatingListboxPosition } from './use-floating-listbox-position';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional secondary text (e.g. role, facility, MAC ID) */
  description?: string;
  /** Optional group label for visual grouping */
  group?: string;
  /**
   * Keep `description` visible on the closed trigger once selected. Person
   * pickers opt in so same-name people stay distinguishable (Hark H1).
   */
  showDescriptionWhenSelected?: boolean;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  required?: boolean;
  onSearchChange?: (search: string) => void;
  serverSearch?: boolean;
  /** Authoritative tenant/cohort key; changing it discards remote label retention. */
  scopeKey?: string;
  statusText?: string;
  /** Optional recovery action inside the panel; retry keeps the current search input. */
  retryAction?: { label: string; onRetry: () => void };
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  density?: FieldDensity;
  size?: FieldControlSize;
  error?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  /** Show a loading spinner inside the dropdown */
  loading?: boolean;
  /** Text to show when no options match the search */
  emptyText?: string;
  /** Minimum characters before filtering starts (0 = filter immediately) */
  minSearchLength?: number;
  /** If true, options are grouped by the `group` field */
  grouped?: boolean;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Stable hook for browser/UAT flows */
  testId?: string;
}

/**
 * SearchableSelect — a typeahead combobox for Statura Care.
 *
 * Designed for both short enum lists and large dynamic lists (staff, clients).
 * - Type to filter options instantly
 * - Keyboard navigation (arrows, Enter, Escape)
 * - Grouped options with section headers
 * - Loading and empty states
 * - ARIA-compliant combobox pattern
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Type to search…',
  className,
  density,
  size,
  error = false,
  disabled = false,
  name,
  id,
  loading = false,
  emptyText = 'No results found',
  minSearchLength = 0,
  grouped = false,
  clearable = false,
  testId,
  required,
  onSearchChange,
  serverSearch = false,
  scopeKey,
  statusText,
  retryAction,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();
  const { position, updatePosition } = useFloatingListboxPosition({
    isOpen,
    triggerRef,
    preferredMaxHeight: 304,
    minHeight: 148,
  });

  const retainedOption = useRef<SearchableSelectOption | undefined>(undefined);
  const retainedScope = useRef(scopeKey);
  if (!serverSearch || retainedScope.current !== scopeKey) retainedOption.current = undefined;
  retainedScope.current = scopeKey;
  const currentOption = options.find((o) => o.value === value);
  if (serverSearch && currentOption) retainedOption.current = currentOption;
  const selectedOption =
    currentOption ??
    (serverSearch && retainedOption.current?.value === value ? retainedOption.current : undefined);

  // ── Filtering ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (serverSearch) return options;
    if (search.length < minSearchLength) return options;
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.description && o.description.toLowerCase().includes(q)),
    );
  }, [options, search, minSearchLength, serverSearch]);

  // ── Grouping ───────────────────────────────────────────────
  const groups = useMemo(() => {
    if (!grouped) return null;
    const map = new Map<string, SearchableSelectOption[]>();
    for (const opt of filtered) {
      const g = opt.group || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(opt);
    }
    return map;
  }, [filtered, grouped]);

  // Flat list for keyboard navigation (order matches render)
  const flatFiltered = useMemo(() => {
    if (!grouped || !groups) return filtered;
    const flat: SearchableSelectOption[] = [];
    for (const [, opts] of groups) {
      flat.push(...opts);
    }
    return flat;
  }, [filtered, grouped, groups]);

  // ── Open / close ───────────────────────────────────────────
  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setSearch('');
    onSearchChange?.('');
    setHighlightedIndex(-1);
    // Focus the search input after render
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [disabled, onSearchChange]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  }, []);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      close();
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, close]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listboxRef.current) {
      const items = listboxRef.current.querySelectorAll('[role="option"]');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const raf = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(raf);
  }, [flatFiltered.length, grouped, isOpen, updatePosition]);

  // ── Selection ──────────────────────────────────────────────
  const select = useCallback(
    (opt: SearchableSelectOption) => {
      if (opt.disabled) return;
      onChange?.(opt.value);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.('');
      close();
    },
    [onChange, close],
  );

  // ── Keyboard ───────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (clearable && !required && !isOpen && (e.key === 'Delete' || e.key === 'Backspace')) {
      e.preventDefault();
      onChange?.('');
      return;
    }

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const opt = flatFiltered[highlightedIndex];
          if (opt && !opt.disabled) select(opt);
        } else if (!isOpen) {
          open();
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          open();
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < flatFiltered.length && flatFiltered[next]?.disabled) next++;
            return next < flatFiltered.length ? next : prev;
          });
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && flatFiltered[next]?.disabled) next--;
            return next >= 0 ? next : prev;
          });
        }
        break;

      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          close();
          triggerRef.current?.focus();
        }
        break;

      case 'Tab':
        if (isOpen) {
          if (retryAction && !loading && e.currentTarget === inputRef.current && !e.shiftKey) {
            e.preventDefault();
            retryRef.current?.focus();
            break;
          }
          // The search input lives at the end of body. Put the browser's native
          // tab origin back on the trigger before unmounting that input. Native
          // Tab order (and an enclosing dialog's focus trap) then does the rest.
          triggerRef.current?.focus();
          close();
        }
        break;
    }
  };

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // ── Render helpers ─────────────────────────────────────────
  const renderOption = (opt: SearchableSelectOption, flatIdx: number) => (
    <li
      key={opt.value}
      id={`${listboxId}-${flatIdx}`}
      role="option"
      aria-selected={opt.value === value}
      aria-disabled={opt.disabled}
      onMouseEnter={() => !opt.disabled && setHighlightedIndex(flatIdx)}
      onClick={() => select(opt)}
      className={cn(
        'flex items-center justify-between px-3 py-2 font-body text-sm cursor-pointer transition-interactive',
        flatIdx === highlightedIndex && 'bg-mist',
        opt.value === value && 'text-action font-medium',
        opt.disabled && 'cursor-not-allowed opacity-50',
        !opt.disabled && 'text-text-heading',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate">{opt.label}</div>
        {opt.description && (
          <div className="truncate text-xs text-text-muted">{opt.description}</div>
        )}
      </div>
      {opt.value === value && <Check className="ml-2 h-4 w-4 shrink-0 text-action" />}
    </li>
  );

  let optionIdx = 0;

  const dropdown =
    isOpen && position && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              // A dropdown must render above whatever opened it.
              //
              // Owner, 2026-08-26, on live production: "sitewide that dropdowns
              // are not visible", "I hit the dropdown issue on many surfaces",
              // "a lot of them were on popups such as the log time wizard".
              //
              // This panel portals to document.body with position:fixed, which
              // takes it out of every clipping ancestor - that part was right. But
              // it sat at z-index 80 while dialog.tsx uses z-[200], the create-shift
              // host z-[210] and date-entry z-[220]. So inside ANY modal the panel
              // opened behind the modal: present in the DOM, present in the
              // accessibility tree, invisible on screen. The Log Time wizard is
              // exactly that shape.
              //
              // 300 sits above every overlay in the app. A transient, dismissible
              // popover belongs at the top of the stack by definition - it is not
              // competing with the modal, it is owned by it.
              zIndex: 300,
              transformOrigin: position.placement === 'top' ? 'bottom' : 'top',
            }}
            className="flex flex-col overflow-hidden rounded-overlay border border-border bg-surface-sheet shadow-popover"
          >
            {/* Search input */}
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-text-muted" />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label={searchPlaceholder}
                aria-expanded={true}
                aria-controls={listboxId}
                aria-activedescendant={
                  highlightedIndex >= 0 ? `${listboxId}-${highlightedIndex}` : undefined
                }
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  onSearchChange?.(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="flex-1 bg-transparent font-body text-sm text-text-heading placeholder:text-text-muted outline-none"
                data-testid={testId ? `${testId}-search` : undefined}
                autoComplete="off"
                spellCheck={false}
              />
              {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-text-muted" />}
            </div>

            {statusText ? (
              <p role="status" className="shrink-0 px-3 py-2 text-xs text-text-muted">
                {statusText}
              </p>
            ) : null}
            {retryAction && (
              <button
                ref={retryRef}
                type="button"
                className="shrink-0 px-3 py-2 text-left text-sm underline focus-visible:outline focus-visible:outline-2"
                disabled={loading}
                onClick={() => {
                  inputRef.current?.focus();
                  retryAction.onRetry();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Tab' && event.shiftKey) {
                    event.preventDefault();
                    inputRef.current?.focus();
                  } else if (event.key === 'Tab' || event.key === 'Escape') {
                    handleKeyDown(event);
                  }
                }}
              >
                {retryAction.label}
              </button>
            )}
            {/* Options list */}
            <ul
              id={listboxId}
              ref={listboxRef}
              role="listbox"
              aria-label={ariaLabel ?? placeholder}
              style={{ maxHeight: Math.max(48, position.maxHeight - 45) }}
              className="min-h-0 overflow-auto py-1"
            >
              {flatFiltered.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-text-muted">
                  {loading ? 'Loading…' : emptyText}
                </li>
              ) : grouped && groups ? (
                // Grouped rendering
                Array.from(groups.entries()).map(([groupLabel, groupOpts]) => (
                  <li key={groupLabel}>
                    <div className="sticky top-0 bg-surface-sheet px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
                      {groupLabel}
                    </div>
                    <ul role="group" aria-label={groupLabel}>
                      {groupOpts.map((opt) => {
                        const idx = optionIdx++;
                        return renderOption(opt, idx);
                      })}
                    </ul>
                  </li>
                ))
              ) : (
                // Flat rendering
                flatFiltered.map((opt, idx) => renderOption(opt, idx))
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className={cn('relative', className)} data-testid={testId}>
      {name &&
        (required ? (
          <input
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            required
            disabled={disabled}
            name={name}
            value={value ?? ''}
            onChange={() => undefined}
            onInvalid={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          />
        ) : (
          <input type="hidden" disabled={disabled} name={name} value={value ?? ''} />
        ))}

      {/* ── Trigger button ─────────────────────────────────── */}
      <Button
        variant="outline"
        size={size}
        density={density}
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-label={ariaLabel ?? (id ? undefined : placeholder)}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error || ariaInvalid}
        aria-required={required}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        disabled={disabled}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={cn(
          fieldControlClassName,
          fieldDensityClassName(density, size),
          'items-center justify-between text-left hover:border-focus',
          error && 'border-status-danger-fg focus:border-status-danger-fg',
          disabled && 'cursor-not-allowed opacity-50',
          selectedOption ? 'text-text-heading' : 'text-text-muted',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {selectedOption?.showDescriptionWhenSelected && selectedOption.description && (
            <span className="block truncate text-xs text-text-muted">
              {selectedOption.description}
            </span>
          )}
        </span>
        <div className="ml-2 flex items-center gap-1">
          {clearable && !required && selectedOption && !disabled && (
            <span
              aria-label="Clear selection (Delete)"
              role="button"
              tabIndex={-1}
              onClick={handleClear}
              className="rounded p-0.5 hover:bg-surface-soft"
            >
              <X className="h-3.5 w-3.5 text-text-muted" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-text-muted transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </Button>
      {dropdown}
    </div>
  );
}
